import { computed } from "vue";
import { useStore } from "vuex";
import { trackMobileWebEvent } from "@/lib/pendo";

import { useMobileWebOptimization } from "@/composables/useMobileWebOptimization";
import { AdminPreference } from "@/typings/adminPreferences";

export const DESKTOP_PREFERENCE_KEY = "scheduler_2";
export const PHONE_PREFERENCE_KEY = "scheduler_2_mobile";

type SchedulerVersion = "scheduler_2" | "old_scheduler";
type SwitchSurface = "nav" | "banner" | "settings";

/**
 * Which scheduler this viewport class has chosen, and how to change it.
 *
 * The desktop and phone choices are two admin preferences. A phone width with
 * the mobile web layout on reads and writes the phone key; everything else the
 * desktop key, which is the single key the board used before the split. The
 * key follows the store rather than being captured at setup: on a cold load
 * the tenant flags land after setup, and a switch tapped after a mid-session
 * resize edits the class the admin is looking at.
 *
 * ScheduleIndex derives the page from this, so crossing the breakpoint re-picks
 * the board for the class the admin is now looking at; an explicit switch still
 * reloads, the way the nav item always has.
 */
export function useSchedulerChoice() {
  const store = useStore();
  const { deviceClass } = useMobileWebOptimization();

  const preferenceKey = computed(() =>
    deviceClass.value === "phone"
      ? PHONE_PREFERENCE_KEY
      : DESKTOP_PREFERENCE_KEY
  );

  const scheduler2Enabled = computed(
    () =>
      store.state.adminPreferences.find(
        (pref: AdminPreference) => pref.key === preferenceKey.value
      )?.enabled === true
  );

  const switchTo = async (
    version: SchedulerVersion,
    surface: SwitchSurface
  ) => {
    const action =
      version === "scheduler_2" ? "enablePreference" : "disablePreference";
    await store.dispatch(action, preferenceKey.value);
    trackMobileWebEvent("schedule_version_switch", { to: version, surface });
    window.location.reload();
  };

  return { deviceClass, scheduler2Enabled, switchTo };
}
